import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  CHAT_EASE,
  getChatVisualTokens,
} from "./chatVisual";

/**
 * Conversational message.
 * - hero: large centered opening question (before first answer)
 * - chat: app left / user right once the conversation is underway
 */
export default function ChatMessage({
  role = "app",
  children,
  variant = "chat",
  isLatest = false,
}) {
  const theme = useTheme();
  const tokens = getChatVisualTokens(theme);
  const isUser = role === "user";
  const isHero = variant === "hero" && !isUser;

  return (
    <Box
      data-testid={isUser ? "onboarding-chat-user-message" : "onboarding-chat-app-message"}
      data-role={role}
      data-variant={isHero ? "hero" : "chat"}
      data-latest={isLatest ? "true" : "false"}
      sx={{
        display: "flex",
        justifyContent: isHero ? "center" : isUser ? "flex-end" : "flex-start",
        width: "100%",
        // Tighter within a Q→A pair; more air before the next question.
        mb: isHero ? 0 : isUser ? 3.25 : isLatest ? 1.75 : 1.1,
        ...(isUser
          ? {
              animation: `onboardingChatInRight 440ms ${CHAT_EASE}`,
              "@keyframes onboardingChatInRight": {
                from: { opacity: 0, transform: "translateX(14px) scale(0.98)" },
                to: { opacity: 1, transform: "translateX(0) scale(1)" },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }
          : !isHero
            ? {
                animation: `onboardingChatInLeft 440ms ${CHAT_EASE}`,
                "@keyframes onboardingChatInLeft": {
                  from: { opacity: 0, transform: "translateX(-10px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }
            : {
                animation: `onboardingHeroIn 440ms ${CHAT_EASE}`,
                "@keyframes onboardingHeroIn": {
                  from: { opacity: 0, transform: "translateY(10px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }),
      }}
    >
      <Box
        sx={{
          maxWidth: isHero ? 560 : isUser ? "76%" : "90%",
          px: isUser ? 2.1 : isHero ? 1.5 : 0,
          py: isUser ? 1.2 : 0,
          borderRadius: isUser ? "20px 20px 6px 20px" : 0,
          bgcolor: isUser ? tokens.CHAT_USER_BUBBLE_BG : "transparent",
          border: isUser ? tokens.CHAT_USER_BUBBLE_BORDER : "none",
          boxShadow: isUser ? tokens.CHAT_USER_BUBBLE_SHADOW : "none",
          textAlign: isHero ? "center" : "left",
          transition: `box-shadow 220ms ${CHAT_EASE}, transform 220ms ${CHAT_EASE}`,
          ...(isUser
            ? {
                "&:hover": {
                  boxShadow: tokens.CHAT_USER_BUBBLE_SHADOW,
                },
              }
            : {}),
        }}
      >
        <Typography
          component="p"
          sx={{
            m: 0,
            fontSize: isHero
              ? { xs: "1.45rem", md: "1.8rem" }
              : isUser
                ? { xs: 14, md: 14.5 }
                : isLatest
                  ? { xs: "1.2rem", md: "1.35rem" }
                  : { xs: "0.92rem", md: "0.98rem" },
            fontWeight: isUser ? 600 : isHero ? 500 : isLatest ? 700 : 500,
            lineHeight: isUser ? 1.4 : isHero ? 1.35 : isLatest ? 1.3 : 1.4,
            letterSpacing: isHero
              ? "0.005em"
              : isLatest
                ? "-0.02em"
                : isUser
                  ? "0.01em"
                  : "-0.01em",
            color: isUser
              ? tokens.CHAT_USER_TEXT
              : isHero
                ? tokens.CHAT_HERO_TEXT
                : isLatest
                  ? tokens.CHAT_APP_TEXT
                  : tokens.CHAT_APP_TEXT_MUTED,
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
